import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { displayCurrencyAmount } from "../../utils/display";
import { CHROME, SERIES } from "../../utils/receivablesChartTokens";
import type { PaidArchiveDashboardResponse } from "../../api/queries/paymentsQueries";

type Props = {
  monthly: PaidArchiveDashboardResponse["monthly"];
};

const SERIES_COLORS = [SERIES.primary, SERIES.secondary, "#7C3AED", "#EA580C"];

export function PaidInvoicesChart({ monthly }: Props) {
  const currencies = useMemo(
    () => [...new Set(monthly.map((row) => row.currency))].sort(),
    [monthly],
  );
  const [activeCurrency, setActiveCurrency] = useState<string | null>(null);

  const currency =
    activeCurrency && currencies.includes(activeCurrency)
      ? activeCurrency
      : currencies[0];

  const points = useMemo(() => {
    if (!currency) return [];
    const byMonth = new Map<string, number>();
    for (const row of monthly) {
      if (row.currency !== currency) continue;
      byMonth.set(row.month, (byMonth.get(row.month) ?? 0) + row.amount);
    }
    return [...byMonth.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({ month, amount }));
  }, [monthly, currency]);

  if (monthly.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white text-sm text-gray-500">
        No data for this period
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-800">
          Amount by month
        </h3>
        {currencies.length > 1 ? (
          <div className="flex flex-wrap gap-1">
            {currencies.map((ccy) => (
              <button
                key={ccy}
                type="button"
                onClick={() => setActiveCurrency(ccy)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  ccy === currency
                    ? "bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {ccy}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={CHROME.gridline} vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fill: CHROME.textSecondary, fontSize: 11 }}
              axisLine={{ stroke: CHROME.axis }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: CHROME.textSecondary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={64}
              tickFormatter={(value: number) =>
                displayCurrencyAmount(value, currency ?? "USD")
              }
            />
            <Tooltip
              formatter={(value: number | undefined) =>
                displayCurrencyAmount(Number(value ?? 0), currency ?? "USD")
              }
              contentStyle={{
                borderRadius: 8,
                borderColor: "#E5E7EB",
                fontSize: 12,
              }}
            />
            <Legend />
            <Bar
              dataKey="amount"
              name={currency ?? "Amount"}
              fill={SERIES_COLORS[0]}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
